import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Trophy, Lightning, Check, ChartBar, Clock, Warning, Pause, ArrowDown, Bug, FastForward, ShieldCheck, Timer, ChatCircleText, GearSix, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatScore } from '../lib/scoreFormat';
import ConfirmDialog from '../components/ConfirmDialog';
import MemberLink from '../components/MemberLink';
import EditScoreDialog from '../components/admin/EditScoreDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get('tab') || 'dashboard';
  const { user, logout } = useAuth();

  // Consolidated Admin nav (Batch 2): Members now owns Invites + Pending
  // approvals + impersonation. Redirect any legacy deep links so saved
  // bookmarks keep working.
  //   ?tab=invites        → /admin/members?tab=invites
  //   ?tab=pending | qa   → /admin/members
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'events') { navigate('/admin/events', { replace: true }); return; }
    if (t === 'invites') { navigate('/admin/members?tab=invites', { replace: true }); return; }
    if (t === 'pending' || t === 'qa' || t === 'players') { navigate('/admin/members', { replace: true }); return; }
    if (t === 'presentation') { navigate('/admin', { replace: true }); return; }
    if (t === 'matches') { navigate('/admin?tab=challenges', { replace: true }); return; }
    if (t === 'audit') { navigate('/admin/communications?view=compliance', { replace: true }); return; }
    if (t === 'communications') { navigate('/admin/communications', { replace: true }); return; }
  }, [searchParams, navigate]);

  // Non-admin users are redirected — they never see admin UI
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Only these tabs render inline here; anything else falls back to Dashboard
  // (the effect above redirects relocated/removed sections away).
  const VALID_TABS = ['dashboard', 'challenges', 'comms', 'debug'];
  const activeTab = VALID_TABS.includes(tab) ? tab : 'dashboard';

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">Admin</h1>
          <p className="text-sm text-[#6a766e] mt-1">Manage ladder operations</p>
        </div>
        {/* Global Sign Out entry for admins. The bottom nav replaces
            Settings with Admin for admin users, so this button ensures
            admins can still reach the canonical logout function from
            their primary landing surface. Uses the same useAuth().logout. */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="admin-settings-link"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-[#013523] bg-white border border-[#e3dcd1] hover:border-[#013523]/40 min-h-[36px]"
          >
            <GearSix size={14} weight="bold" /> Settings
          </button>
          <button
            type="button"
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 min-h-[36px]"
          >
            <SignOut size={14} weight="bold" /> Sign Out
          </button>
        </div>
      </div>

      {/* Admin nav is now rendered persistently by AppLayout (AdminNav),
          so it shows on every admin page — not just here. */}

      {activeTab === 'dashboard' && <DashboardTab />}
      {/* Batch 2: 'pending', 'invites' and 'qa' tabs removed — consolidated
          into /admin/members (Invites tab + Awaiting approval + impersonation).
          Legacy ?tab= deep links are redirected above. */}
      {activeTab === 'challenges' && <ChallengesTab />}
      {activeTab === 'comms' && <CommsTab />}
      {activeTab === 'debug' && <DebugTab />}
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminAPI.getDashboard();
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#015e3f] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-sm text-[#6a766e]">Failed to load dashboard</p>;

  return (
    <div className="space-y-5" data-testid="admin-dashboard-tab">
      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2">
        <StatBox label="Active" value={data.stats.total_active} />
        <StatBox label="Paused" value={data.stats.total_paused} />
        <StatBox label="Challenges" value={data.stats.active_challenges} />
        <StatBox label="Pending" value={data.stats.pending_approvals} />
      </div>

      {/* Accepted challenges — no deadline post-launch. Kept as a simple
          "awaiting match" list so admins can still see the pairings. */}
      {data.accepted_challenges.length > 0 && (
        <Section title="Accepted — Awaiting Match" icon={Clock} count={data.accepted_challenges.length}>
          {data.accepted_challenges.map(ch => (
            <div key={ch.challenge_id} className="bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2.5 flex justify-between items-center" data-testid={`admin-accepted-row-${ch.challenge_id}`}>
              <div>
                <div className="text-sm text-[#2a332c]"><MemberLink profileId={ch.challenger_id} name={ch.challenger_name} /> <span className="text-[#6a766e]">vs</span> <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} /></div>
                <div className="text-xs text-[#6a766e]">#{ch.challenger_rank} vs #{ch.challenged_rank}</div>
              </div>
              {ch.accepted_at && (
                <div className="text-right text-[10px] text-[#6a766e]" data-testid={`admin-accepted-since-${ch.challenge_id}`}>
                  Accepted {new Date(ch.accepted_at).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Pending challenges */}
      {data.pending_challenges.length > 0 && (
        <Section title="Pending — Awaiting Response" icon={Lightning} count={data.pending_challenges.length}>
          {data.pending_challenges.map(ch => (
            <div key={ch.challenge_id} className="bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2.5 flex justify-between items-center">
              <div>
                <div className="text-sm text-[#2a332c]"><MemberLink profileId={ch.challenger_id} name={ch.challenger_name} /> <span className="text-[#c2a251]">challenged</span> <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} /></div>
              </div>
              <span className={`text-xs font-mono ${ch.hours_remaining < 12 ? 'text-red-500' : 'text-[#6a766e]'}`}>
                {ch.hours_remaining}h left
              </span>
            </div>
          ))}
        </Section>
      )}

      {/* Pending score approvals */}
      {data.pending_scores.length > 0 && (
        <Section title="Pending Score Approvals" icon={Trophy} count={data.pending_scores.length}>
          {data.pending_scores.map(m => (
            <div key={m.match_id} className="bg-[#c2a251]/5 rounded-xl border border-[#c2a251]/30 px-3 py-2.5">
              <div className="text-sm text-[#2a332c]"><MemberLink profileId={m.player1_id} name={m.player1_name} /> vs <MemberLink profileId={m.player2_id} name={m.player2_name} /></div>
              <div className="text-xs text-[#6a766e]">Score: {formatScore(m.score)} | Awaiting opponent approval</div>
            </div>
          ))}
        </Section>
      )}

      {/* At-risk / inactive players */}
      {data.at_risk_players.length > 0 && (
        <Section title="Inactivity Watch" icon={Warning} count={data.at_risk_players.length}>
          {data.at_risk_players.map(p => (
            <div key={p.player_id} className={`rounded-xl border px-3 py-2.5 flex justify-between items-center ${p.penalty_due ? 'bg-red-50 border-red-200' : 'bg-[#fbfaf8] border-[#e3dcd1]'}`}>
              <div>
                <div className="text-sm text-[#2a332c]">#{p.rank} {p.name}</div>
                <div className="text-xs text-[#6a766e]">{p.wins}W-{p.losses}L</div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-medium ${p.penalty_due ? 'text-red-500' : 'text-[#c2a251]'}`}>
                  {p.days_inactive}d inactive
                </div>
                {p.penalty_due && <div className="text-[10px] text-red-400">Penalty due</div>}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Paused players */}
      {data.paused_players.length > 0 && (
        <Section title="Paused Players" icon={Pause} count={data.paused_players.length}>
          {data.paused_players.map(p => (
            <div key={p.player_id} className="bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2.5 opacity-70">
              <div className="text-sm text-[#6a766e]">{p.name} — {p.wins}W-{p.losses}L</div>
            </div>
          ))}
        </Section>
      )}

      {/* Recent results */}
      {data.recent_results.length > 0 && (
        <Section title="Recent Results" icon={Trophy} count={data.recent_results.length}>
          {data.recent_results.slice(0, 5).map(m => {
            const winnerName = m.winner_id === m.player1_id ? m.player1_name : m.player2_name;
            const loserName = m.winner_id === m.player1_id ? m.player2_name : m.player1_name;
            return (
              <div key={m.match_id} className="bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2.5">
                <div className="text-sm text-[#2a332c]">
                  <MemberLink profileId={m.winner_id} name={winnerName} className="text-[#015e3f] font-medium" /> def. <MemberLink profileId={m.winner_id === m.player1_id ? m.player2_id : m.player1_id} name={loserName} />
                </div>
                <div className="text-xs font-mono text-[#6a766e]">{formatScore(m.score)}</div>
              </div>
            );
          })}
        </Section>
      )}

      {/* Activity feed */}
      {data.recent_events.length > 0 && (
        <Section title="Activity Log" icon={ArrowDown} count={data.recent_events.length}>
          {data.recent_events.slice(0, 10).map(evt => (
            <div key={evt.event_id} className="px-3 py-2 border-l-2 border-[#e3dcd1] ml-1">
              <div className="text-sm text-[#2a332c]">{evt.description}</div>
              <div className="text-[10px] text-[#6a766e] mt-0.5">
                {new Date(evt.created_at).toLocaleString()} · {evt.event_type.replace(/_/g, ' ')}
              </div>
            </div>
          ))}
        </Section>
      )}

      {data.accepted_challenges.length === 0 && data.pending_challenges.length === 0 && data.at_risk_players.length === 0 && data.recent_events.length === 0 && (
        <div className="text-center py-12">
          <ChartBar size={40} weight="duotone" className="text-[#e3dcd1] mx-auto mb-3" />
          <p className="text-[#6a766e]">All clear. No active issues.</p>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-[#f0ece3] rounded-xl border border-[#e3dcd1] p-3 text-center">
      <div className="font-serif text-2xl text-[#013523] font-semibold">{value}</div>
      <div className="text-[10px] tracking-[0.08em] uppercase text-[#6a766e]">{label}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="duotone" className="text-[#015e3f]" />
        <h3 className="text-sm font-medium text-[#013523]">{title}</h3>
        <span className="text-[10px] bg-[#f0ece3] text-[#6a766e] rounded-full px-1.5 py-0.5">{count}</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// ============================================================================
// Phase E1.4: legacy `EventsTab` + `CreateSessionDialogLazy` removed.
// The Admin Events Hub at `/admin/events` is now the single source of truth
// (see /app/frontend/src/pages/AdminEventsHubPage.js). Clicking the "Events"
// admin tab redirects there; deep links to `?tab=events` also redirect.
// ============================================================================


function ChallengesTab() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // 'active' | 'completed' | 'all'
  const [pendingCancel, setPendingCancel] = useState(null); // challenge
  const [editChallenge, setEditChallenge] = useState(null); // challenge whose match is being corrected

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await adminAPI.getChallenges();
      setChallenges(res.data.challenges || []);
    } catch (err) {
      console.error('admin challenges fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const doCancel = async (id) => {
    try {
      await adminAPI.cancelChallenge(id);
      toast.success('Challenge canceled');
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const activeSet = new Set(['pending', 'accepted']);
  const filtered = challenges.filter((ch) => {
    if (filter === 'active') return activeSet.has(ch.status);
    if (filter === 'completed') return !activeSet.has(ch.status);
    return true;
  });

  const filters = [
    { id: 'active', label: 'Active' },
    { id: 'completed', label: 'History' },
    { id: 'all', label: 'All' },
  ];

  return (
    <div className="space-y-3" data-testid="admin-challenges-tab">
      <div className="flex gap-1 bg-[#f0ece3] p-1 rounded-xl">
        {filters.map((f) => (
          <button
            key={f.id}
            data-testid={`admin-challenges-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
              filter === f.id ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm' : 'text-[#6a766e]'
            }`}
          >
            {f.label}
            <span className="ml-1 text-[10px] opacity-70">
              ({challenges.filter((ch) => {
                if (f.id === 'active') return activeSet.has(ch.status);
                if (f.id === 'completed') return !activeSet.has(ch.status);
                return true;
              }).length})
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-[#6a766e] text-center py-8">Loading challenges…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#6a766e] text-center py-8">No challenges</p>
        ) : null}
        {!loading && filtered.map((ch) => (
          <div key={ch.challenge_id} className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] px-4 py-3" data-testid={`admin-challenge-row-${ch.challenge_id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#2a332c] truncate">
                  <MemberLink profileId={ch.challenger_id} name={ch.challenger_name} /> <span className="text-[#6a766e] font-normal">vs</span> <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} />
                </div>
                <div className="text-xs text-[#6a766e] mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                  <span>Created {new Date(ch.created_at).toLocaleDateString()}</span>
                  {ch.accepted_at && <span>· Accepted {new Date(ch.accepted_at).toLocaleDateString()}</span>}
                  {/* Show the acceptance expiry ONLY while the challenge is
                      still pending. Once accepted, there is no deadline —
                      the pending-window `expires_at` is stale and must not
                      be rendered. */}
                  {ch.expires_at && ch.status === 'pending' && (
                    <span>· Expires {new Date(ch.expires_at).toLocaleDateString()}</span>
                  )}
                  {ch.match && (
                    <span>· Score {formatScore(ch.match.score)} → {ch.match.winner_name}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                  ch.status === 'pending' ? 'bg-[#c2a251]/20 text-[#c2a251]' :
                  ch.status === 'accepted' ? 'bg-[#015e3f]/10 text-[#015e3f]' :
                  ch.status === 'completed' ? 'bg-[#015e3f]/10 text-[#015e3f]' :
                  ch.status === 'expired' ? 'bg-[#6a766e]/10 text-[#6a766e]' :
                  'bg-red-50 text-red-500'
                }`}>
                  {ch.status}
                </span>
                {activeSet.has(ch.status) && (
                  <button
                    data-testid={`admin-cancel-challenge-${ch.challenge_id}`}
                    onClick={() => setPendingCancel(ch)}
                    className="text-xs bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-1 font-medium active:scale-95 hover:bg-red-100"
                  >
                    Cancel
                  </button>
                )}
                {ch.match && ch.match.status === 'approved' && (
                  <button
                    data-testid={`admin-edit-score-${ch.challenge_id}`}
                    onClick={() => setEditChallenge(ch)}
                    className="text-xs bg-white text-[#015e3f] border border-[#015e3f]/30 rounded-full px-3 py-1 font-medium active:scale-95 hover:bg-[#015e3f]/5"
                  >
                    Edit score
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!pendingCancel}
        title="Cancel challenge"
        message={
          pendingCancel
            ? `Are you sure you want to cancel the challenge between ${pendingCancel.challenger_name} and ${pendingCancel.challenged_name}? Both players will return to the pool.`
            : ''
        }
        confirmLabel="Cancel challenge"
        destructive
        onConfirm={() => { const c = pendingCancel; setPendingCancel(null); doCancel(c.challenge_id); }}
        onCancel={() => setPendingCancel(null)}
        testId="admin-cancel-challenge-dialog"
      />

      <EditScoreDialog
        open={!!editChallenge}
        challenge={editChallenge}
        onClose={() => setEditChallenge(null)}
        onSaved={fetchChallenges}
      />
    </div>
  );
}




function DebugTab() {
  const [players, setPlayers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        adminAPI.getPlayers(),
        adminAPI.getChallenges(),
      ]);
      setPlayers(pRes.data.players);
      setChallenges(cRes.data.challenges.filter(c => ['pending', 'accepted'].includes(c.status)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addLog = (msg) => {
    setLog(prev => [{ text: msg, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 30));
  };

  const runAction = async (key, fn) => {
    setActionLoading(key);
    try {
      const res = await fn();
      const msg = res.data.message || JSON.stringify(res.data);
      addLog(msg);
      toast.success(msg);
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Action failed';
      addLog(`ERROR: ${detail}`);
      toast.error(detail);
    } finally {
      setActionLoading(null);
    }
  };

  const runIntegrity = async () => {
    setActionLoading('integrity');
    try {
      const res = await adminAPI.debug.integrityCheck();
      setIntegrity(res.data);
      addLog(`Integrity check: ${res.data.status.toUpperCase()} — ${res.data.issues.length} issue(s)`);
    } catch (err) {
      toast.error('Integrity check failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#015e3f] border-t-transparent rounded-full animate-spin" /></div>;

  const activePlayers = players.filter(p => p.status === 'active');
  const pausedPlayers = players.filter(p => p.status === 'paused');
  const pendingChallenges = challenges.filter(c => c.status === 'pending');
  const acceptedChallenges = challenges.filter(c => c.status === 'accepted');

  return (
    <div className="space-y-5" data-testid="admin-debug-tab">
      {/* Warning banner */}
      <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-2 text-red-600 text-xs font-medium">
          <Bug size={16} weight="fill" />
          ADMIN / QA DEBUG PANEL — Actions modify live ladder data
        </div>
      </div>

      {/* Integrity Check */}
      <DebugSection title="Ladder Integrity Check" icon={ShieldCheck}>
        <button
          data-testid="run-integrity-check"
          onClick={runIntegrity}
          disabled={actionLoading === 'integrity'}
          className="w-full bg-[#015e3f] text-white rounded-xl px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50"
        >
          {actionLoading === 'integrity' ? 'Checking...' : 'Run Integrity Check'}
        </button>
        {integrity && (
          <div className={`mt-3 rounded-xl border p-3 ${integrity.status === 'pass' ? 'bg-[#015e3f]/5 border-[#015e3f]/30' : 'bg-red-50 border-red-200'}`} data-testid="integrity-result">
            <div className={`text-sm font-medium ${integrity.status === 'pass' ? 'text-[#015e3f]' : 'text-red-600'}`}>
              {integrity.status === 'pass' ? 'PASS — No issues found' : `FAIL — ${integrity.issues.length} issue(s)`}
            </div>
            <div className="text-xs text-[#6a766e] mt-1">
              {integrity.total_active} active · {integrity.total_paused} paused · {integrity.active_challenges} active challenges
            </div>
            {integrity.issues.length > 0 && (
              <div className="mt-2 space-y-1">
                {integrity.issues.map((issue, i) => (
                  <div key={i} className={`text-xs px-2 py-1 rounded ${issue.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-[#c2a251]/10 text-[#c2a251]'}`}>
                    [{issue.severity}] {issue.detail}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DebugSection>

      {/* Run Expiration Tasks */}
      <DebugSection title="Run Scheduled Tasks" icon={Timer}>
        <button
          data-testid="run-expiration-tasks"
          onClick={() => runAction('expiration', adminAPI.debug.runExpiration)}
          disabled={actionLoading === 'expiration'}
          className="w-full bg-[#f0ece3] text-[#013523] border border-[#e3dcd1] rounded-xl px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50"
        >
          {actionLoading === 'expiration' ? 'Running...' : 'Trigger 72h / 7d / 30d Expiration Check'}
        </button>
        <p className="text-[10px] text-[#6a766e] mt-1">Runs pending-challenge expiration, match-deadline penalties, and inactivity penalties.</p>
      </DebugSection>

      {/* Challenge Simulation */}
      {(pendingChallenges.length > 0 || acceptedChallenges.length > 0) && (
        <DebugSection title="Simulate Challenge Expiration" icon={FastForward}>
          {pendingChallenges.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-medium">Pending (expire = no penalty)</div>
              {pendingChallenges.map(ch => (
                <div key={ch.challenge_id} className="flex items-center justify-between bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2">
                  <div className="text-xs text-[#2a332c]">{ch.challenger_name} → {ch.challenged_name}</div>
                  <button
                    data-testid={`debug-expire-pending-${ch.challenge_id}`}
                    onClick={() => runAction(`ep-${ch.challenge_id}`, () => adminAPI.debug.expirePending(ch.challenge_id))}
                    disabled={!!actionLoading}
                    className="text-[10px] bg-[#c2a251]/10 text-[#c2a251] border border-[#c2a251]/30 rounded-full px-2.5 py-1 font-medium active:scale-95 disabled:opacity-50"
                  >
                    Expire
                  </button>
                </div>
              ))}
            </div>
          )}
          {acceptedChallenges.length > 0 && (
            <div className="space-y-1.5 mt-3">
              <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-medium">Accepted (expire = both -1 rank)</div>
              {acceptedChallenges.map(ch => (
                <div key={ch.challenge_id} className="flex items-center justify-between bg-[#fbfaf8] rounded-xl border border-red-200 px-3 py-2">
                  <div className="text-xs text-[#2a332c]">{ch.challenger_name} vs {ch.challenged_name}</div>
                  <button
                    data-testid={`debug-expire-accepted-${ch.challenge_id}`}
                    onClick={() => runAction(`ea-${ch.challenge_id}`, () => adminAPI.debug.expireAccepted(ch.challenge_id))}
                    disabled={!!actionLoading}
                    className="text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-full px-2.5 py-1 font-medium active:scale-95 disabled:opacity-50"
                  >
                    Expire (-1 rank)
                  </button>
                </div>
              ))}
            </div>
          )}
        </DebugSection>
      )}

      {/* Player Simulation: Inactivity */}
      <DebugSection title="Simulate Inactivity" icon={Warning}>
        <div className="space-y-1.5">
          {activePlayers.map(p => (
            <div key={p.player_id} className="flex items-center justify-between bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2">
              <div className="text-xs text-[#2a332c]">#{p.rank} {p.name}</div>
              <div className="flex gap-1">
                <button
                  data-testid={`debug-set-inactive-${p.player_id}`}
                  onClick={() => runAction(`si-${p.player_id}`, () => adminAPI.debug.setInactive(p.player_id))}
                  disabled={!!actionLoading}
                  className="text-[10px] bg-[#c2a251]/10 text-[#c2a251] border border-[#c2a251]/30 rounded-full px-2 py-1 font-medium active:scale-95 disabled:opacity-50"
                >
                  Flag 31d
                </button>
                <button
                  data-testid={`debug-apply-inactivity-${p.player_id}`}
                  onClick={() => runAction(`ai-${p.player_id}`, () => adminAPI.debug.applyInactivity(p.player_id))}
                  disabled={!!actionLoading}
                  className="text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-1 font-medium active:scale-95 disabled:opacity-50"
                >
                  -4 Ranks
                </button>
              </div>
            </div>
          ))}
        </div>
      </DebugSection>

      {/* Pause/Resume */}
      <DebugSection title="Simulate Pause / Resume" icon={Pause}>
        {activePlayers.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-medium">Active → Pause</div>
            {activePlayers.map(p => (
              <div key={p.player_id} className="flex items-center justify-between bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2">
                <div className="text-xs text-[#2a332c]">#{p.rank} {p.name}</div>
                <button
                  data-testid={`debug-pause-${p.player_id}`}
                  onClick={() => runAction(`pause-${p.player_id}`, () => adminAPI.debug.pausePlayer(p.player_id))}
                  disabled={!!actionLoading}
                  className="text-[10px] bg-[#6a766e]/10 text-[#6a766e] border border-[#6a766e]/30 rounded-full px-2.5 py-1 font-medium active:scale-95 disabled:opacity-50"
                >
                  Pause
                </button>
              </div>
            ))}
          </div>
        )}
        {pausedPlayers.length > 0 && (
          <div className="space-y-1.5 mt-3">
            <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-medium">Paused — Set Duration & Resume</div>
            <div className="text-[10px] text-[#6a766e] mb-1 bg-[#f0ece3] rounded-lg px-2 py-1.5 border border-[#e3dcd1]">
              Penalty tiers: 1-7d = same rank | 8-14d = -1 | 15-21d = -2 | 22-30d = -3 | 31+d = last
            </div>
            {pausedPlayers.map(p => (
              <PausedPlayerRow key={p.player_id} player={p} runAction={runAction} actionLoading={actionLoading} />
            ))}
          </div>
        )}
      </DebugSection>

      {/* Action Log */}
      {log.length > 0 && (
        <DebugSection title="Action Log" icon={ArrowDown}>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {log.map((entry, i) => (
              <div key={i} className="text-xs border-l-2 border-[#e3dcd1] pl-2 py-1">
                <span className="text-[#6a766e] font-mono">{entry.time}</span>{' '}
                <span className={`text-[#2a332c] ${entry.text.startsWith('ERROR') ? 'text-red-600' : ''}`}>{entry.text}</span>
              </div>
            ))}
          </div>
        </DebugSection>
      )}
    </div>
  );
}

function PausedPlayerRow({ player: p, runAction, actionLoading }) {
  const [days, setDays] = useState('');
  const presets = [3, 10, 18, 25, 35];

  return (
    <div className="bg-[#fbfaf8] rounded-xl border border-[#e3dcd1] px-3 py-2.5">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs text-[#2a332c] font-medium">{p.name}</div>
          <div className="text-[10px] text-[#6a766e]">
            Was #{p.paused_rank || '?'} · Paused {p.paused_at ? `${Math.floor((Date.now() - new Date(p.paused_at).getTime()) / 86400000)}d ago` : 'unknown'}
          </div>
        </div>
        <button
          data-testid={`debug-resume-${p.player_id}`}
          onClick={() => runAction(`resume-${p.player_id}`, () => adminAPI.debug.resumePlayer(p.player_id))}
          disabled={!!actionLoading}
          className="text-[10px] bg-[#015e3f] text-white rounded-full px-3 py-1 font-medium active:scale-95 disabled:opacity-50"
        >
          Resume Now
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-[#6a766e] shrink-0">Set to:</span>
        {presets.map(d => (
          <button
            key={d}
            data-testid={`debug-set-pause-${p.player_id}-${d}d`}
            onClick={() => runAction(`spd-${p.player_id}-${d}`, () => adminAPI.debug.setPauseDuration(p.player_id, d))}
            disabled={!!actionLoading}
            className="text-[10px] bg-[#f0ece3] text-[#013523] border border-[#e3dcd1] rounded-full px-2 py-0.5 font-medium active:scale-95 disabled:opacity-50"
          >
            {d}d
          </button>
        ))}
        <input
          type="number"
          value={days}
          onChange={e => setDays(e.target.value)}
          placeholder="#"
          className="w-10 text-[10px] border border-[#e3dcd1] rounded-lg px-1.5 py-0.5 text-center"
          min={1}
        />
        {days && (
          <button
            onClick={() => { runAction(`spd-${p.player_id}-${days}`, () => adminAPI.debug.setPauseDuration(p.player_id, parseInt(days))); setDays(''); }}
            disabled={!!actionLoading}
            className="text-[10px] bg-[#c2a251]/10 text-[#c2a251] border border-[#c2a251]/30 rounded-full px-2 py-0.5 font-medium active:scale-95 disabled:opacity-50"
          >
            Set
          </button>
        )}
      </div>
    </div>
  );
}

function DebugSection({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="duotone" className="text-[#015e3f]" />
        <h3 className="text-sm font-medium text-[#013523]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/**
 * Communications tab — admin-only control over the global SMS mode.
 *
 * Modes:
 *   off      — backend logs [SMS][OFF] and never calls Twilio.
 *   dry_run  — backend logs the body but never calls Twilio (current default).
 *   live     — backend will call Twilio for opted-in users with a phone.
 *
 * Switching to Live always shows a confirmation dialog so it can never be
 * triggered by a stray click.
 */
function CommsTab() {
  const [mode, setMode] = useState(null);
  const [validModes, setValidModes] = useState(['off', 'dry_run', 'live']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingLive, setPendingLive] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getSmsSettings();
      setMode(res.data.sms_mode);
      if (Array.isArray(res.data.valid_modes) && res.data.valid_modes.length) {
        setValidModes(res.data.valid_modes);
      }
    } catch {
      toast.error('Could not load SMS settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persistMode = async (newMode) => {
    setSaving(true);
    try {
      const res = await adminAPI.setSmsSettings(newMode);
      setMode(res.data.sms_mode);
      toast.success(`SMS mode set to ${LABEL[newMode] || newMode}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update SMS mode');
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = (newMode) => {
    if (newMode === mode || saving) return;
    if (newMode === 'live') {
      setPendingLive(true);
      return;
    }
    persistMode(newMode);
  };

  return (
    <div className="space-y-4" data-testid="comms-tab">
      <div>
        <h2 className="font-serif text-2xl text-[#013523] tracking-tight">Communications</h2>
        <p className="text-xs text-[#6a766e] mt-1">SMS sending behavior for the entire app.</p>
      </div>

      <div className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#f0ece3] flex items-center justify-center shrink-0">
            <ChatCircleText size={20} className="text-[#015e3f]" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-[#013523] tracking-tight">SMS Mode</h3>
            <p className="text-xs text-[#6a766e] mt-1 leading-relaxed">
              Controls whether the app sends real text messages, just logs them, or
              does nothing at all. Default is <strong>Dry Run</strong>.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[#6a766e]">Loading…</div>
        ) : (
          <>
            <div
              data-testid="sms-mode-segmented"
              className="grid grid-cols-3 gap-1 bg-[#f0ece3] p-1 rounded-xl"
            >
              {validModes.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`sms-mode-option-${m}`}
                  onClick={() => handleSelect(m)}
                  disabled={saving}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    mode === m
                      ? (m === 'live'
                          ? 'bg-red-600 text-white shadow-sm'
                          : 'bg-[#fbfaf8] text-[#015e3f] shadow-sm')
                      : 'text-[#6a766e] hover:text-[#013523]'
                  } disabled:opacity-60`}
                >
                  {LABEL[m] || m}
                </button>
              ))}
            </div>

            <p
              data-testid="sms-mode-description"
              className="text-[12px] text-[#6a766e] mt-3 leading-relaxed"
            >
              {DESCRIPTION[mode] || ''}
            </p>

            <div className="mt-4 pt-4 border-t border-[#e3dcd1]">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6a766e] mb-1">Current mode</p>
              <p
                data-testid="sms-mode-current"
                className={`text-base font-semibold ${
                  mode === 'live' ? 'text-red-600' : 'text-[#013523]'
                }`}
              >
                {LABEL[mode] || mode}
              </p>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={pendingLive}
        title="Enable Live SMS?"
        message="Live SMS will send real text messages to opted-in users when challenge events occur. Are you sure?"
        confirmLabel="Enable Live SMS"
        cancelLabel="Cancel"
        destructive
        testId="sms-live-confirm"
        onCancel={() => setPendingLive(false)}
        onConfirm={() => { setPendingLive(false); persistMode('live'); }}
      />
    </div>
  );
}

const LABEL = { off: 'Off', dry_run: 'Dry Run', live: 'Live' };
const DESCRIPTION = {
  off:     'No messages sent. Backend logs [SMS][OFF] only. Use this to fully disable SMS.',
  dry_run: 'Messages logged but not sent. Backend logs [SMS][DRY-RUN] with the full body. Safe default.',
  live:    'Real text messages sent via Twilio to opted-in users with a phone number. Twilio credentials must be configured.',
};

